import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box, Card, Modal, Snackbar, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ClipLoader from 'react-spinners/ClipLoader';
import { css } from '@emotion/react';
import { apiDocusign, apiget } from '../../service/api';

function DocuSignPage(data) {
  const [docuSignLink, setDocuSignLink] = useState('');
  const [envelopes, setEnvelopes] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchEnvelopes = async (loanGUID) => {
    try {
      setLoadingMessage('Loading envelopes...');
      setLoading(true);
      const response = await apiget(`docusign/list?loanGUID=${loanGUID}`);
      if (response.status === 200) {
        const formattedData = response.data.result.map((item, index) => ({
          id: item._id,
          title: item.title,
          envelopeId: item.envelopeId,
          status: item.status,
          sentDate: item.sentDate,
          completedDate: item.completedDate,
          documentPath: item.documentPath,
          signers: item.signers.map(signer => `${signer.name} (${signer.email})`).join(', '),
          createdBy: item.createdBy,
        }));
        setEnvelopes(formattedData);
      }
    } catch (error) {
      console.error('Failed to fetch envelopes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnvelopes(data.data);
  }, [data]);

  const onRetrieve = async (envelopeId, loanGUID) => {
    try {
      setLoadingMessage('Retrieving envelope...');
      setLoading(true);
      const response = await apiget(`docusign/retrieve/${envelopeId}?loanGUID=${loanGUID}`);
      if (response.data.includes("The DocuSign envelope has not been signed yet")) {
        setSnackbarMessage(response.data);
        setSnackbarOpen(true);
      } else {
        setAlertMessage('Envelope retrieved successfully.');
      }
    } catch (error) {
      console.error('Error retrieving envelope:', error);
      setAlertMessage('Failed to retrieve envelope. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const columns = [
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'envelopeId', headerName: 'Envelope ID', flex: 1 },
    { field: 'status', headerName: 'Status', flex: 1 },
    { field: 'sentDate', headerName: 'Sent Date', flex: 1, type: 'dateTime' },
    { field: 'completedDate', headerName: 'Completed Date', flex: 1, type: 'dateTime' },
    { field: 'signers', headerName: 'Signers', flex: 1 },
    {
      field: 'actions', headerName: 'Actions', flex: 1, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" onClick={() => console.log("Resend", params.row.envelopeId)}>Resend</Button>
          <Button variant="outlined" size="small" onClick={() => onRetrieve(params.row.envelopeId, data.data)}>Retrieve</Button>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Container sx={{ marginBottom: 4 }}>
        <Typography variant="h4" sx={{ marginY: 2 }}>
          Sent DocuSign Envelopes
        </Typography>
        <Card style={{ height: 500, width: '100%' }}>
          <DataGrid rows={envelopes} columns={columns} pageSize={5} />
        </Card>
      </Container>

      <Modal
        open={loading}
        aria-labelledby="loading-modal-title"
        aria-describedby="loading-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <ClipLoader
            css={css`
              display: block;
              margin: 0 auto;
              border-color: red;
            `}
            size={50}
            color={"#123abc"}
            loading={loading}
          />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {loadingMessage}
          </Typography>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DocuSignPage;
